import {
  trackEvent,
  getBreadcrumbs,
  getCorrelationId,
  getLastFailedApi,
  getLastRoute,
} from './appInsights';

export type BlankScreenDetectionType = 'empty_root' | 'zero_height' | 'blank_container' | 'no_root';

export interface BlankScreenTelemetryProps {
  route: string;
  detectionType: BlankScreenDetectionType;
  domChildrenCount: number;
  durationMs?: number;
  tenantAlias?: string;
  buId?: string;
  lastFailedApi?: {
    url: string;
    status: number;
    timestamp: string;
  } | null;
  recentBreadcrumbs?: any[];
  correlationId?: string;
}

export interface WhiteScreenOptions {
  rootSelector?: string;
  ignoreRoutes?: (string | RegExp)[];
  loadingSelectors?: string[];
  tenantAlias?: string;
  buId?: string;
  customCheck?: (root: HTMLElement) => boolean;
}

const DEFAULT_IGNORE_ROUTES: (string | RegExp)[] = [
  '/signin-oidc',
  '/silentrenew',
  '/callback',
  '/login-redirect',
  '/logout',
];

const DEFAULT_LOADING_SELECTORS = [
  '.app-spinner',
  '.spinner',
  '.loading-spinner',
  '[role="progressbar"]',
  '.ant-spin',
  '.MuiCircularProgress-root',
];

let checkTimeoutId: ReturnType<typeof setTimeout> | null = null;
let lastDetectedRoute: string | null = null;

/**
 * Verifica se a rota fornecida deve ser ignorada para evitar falsos positivos
 */
export const isIgnoredRoute = (
  route: string,
  ignoreList: (string | RegExp)[] = DEFAULT_IGNORE_ROUTES
): boolean => {
  if (!route) return false;
  const cleanPath = route.split('?')[0].toLowerCase();
  return ignoreList.some((item) => {
    if (typeof item === 'string') {
      return cleanPath === item.toLowerCase() || cleanPath.startsWith(item.toLowerCase());
    }
    if (item instanceof RegExp) {
      return item.test(cleanPath);
    }
    return false;
  });
};

/**
 * Verifica se existem spinners de carregamento ativos no DOM
 */
export const hasActiveLoadingSpinner = (
  loadingSelectors: string[] = DEFAULT_LOADING_SELECTORS
): boolean => {
  if (typeof document === 'undefined') return false;
  return loadingSelectors.some((selector) => {
    try {
      const el = document.querySelector(selector);
      if (el) {
        // Verificar se está visível
        const style = window.getComputedStyle ? window.getComputedStyle(el) : null;
        if (style && (style.display === 'none' || style.visibility === 'hidden')) {
          return false;
        }
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  });
};

/**
 * Inspeciona o elemento raiz para determinar se o ecrã está em branco
 */
export const checkWhiteScreen = (
  rootSelector: string = '#root',
  options?: WhiteScreenOptions
): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  const currentRoute =
    getLastRoute() ||
    (window.location ? window.location.pathname : '') ||
    '/';

  const ignoreList = options?.ignoreRoutes || DEFAULT_IGNORE_ROUTES;
  if (isIgnoredRoute(currentRoute, ignoreList)) {
    return false;
  }

  const loadingSelectors = options?.loadingSelectors || DEFAULT_LOADING_SELECTORS;
  if (hasActiveLoadingSpinner(loadingSelectors)) {
    return false;
  }

  const root = document.querySelector(rootSelector) as HTMLElement | null;

  let isBlank = false;
  let detectionType: BlankScreenDetectionType = 'empty_root';
  let childCount = 0;

  if (!root) {
    isBlank = true;
    detectionType = 'no_root';
    childCount = 0;
  } else {
    childCount = root.childElementCount;

    if (options?.customCheck) {
      isBlank = options.customCheck(root);
      if (isBlank) detectionType = 'blank_container';
    } else if (childCount === 0) {
      // Nenhum elemento filho renderizado
      isBlank = true;
      detectionType = 'empty_root';
    } else {
      // Verificar se o container ou elementos filhos têm dimensões visíveis nulas
      const rect = root.getBoundingClientRect();
      const style = window.getComputedStyle ? window.getComputedStyle(root) : null;
      const isHidden =
        (style && (style.display === 'none' || style.visibility === 'hidden')) ||
        (rect.width === 0 && rect.height === 0 && root.offsetHeight === 0 && root.scrollHeight === 0);

      // Verificar também se todos os nós filhos diretos estão ocultos
      const allChildrenEmpty = Array.from(root.children).every((child) => {
        const childEl = child as HTMLElement;
        const childStyle = window.getComputedStyle ? window.getComputedStyle(childEl) : null;
        return (
          childStyle &&
          (childStyle.display === 'none' || childStyle.visibility === 'hidden')
        );
      });

      if (isHidden || allChildrenEmpty) {
        isBlank = true;
        detectionType = 'zero_height';
      }
    }
  }

  if (isBlank) {
    // Evitar disparos repetidos consecutivos para a mesma rota
    if (lastDetectedRoute === currentRoute) {
      return true;
    }
    lastDetectedRoute = currentRoute;

    const lastFailedApi = getLastFailedApi();
    const payload: BlankScreenTelemetryProps = {
      route: currentRoute,
      detectionType,
      domChildrenCount: childCount,
      tenantAlias: options?.tenantAlias,
      buId: options?.buId,
      lastFailedApi: lastFailedApi
        ? {
            url: lastFailedApi.url,
            status: lastFailedApi.status,
            timestamp: lastFailedApi.timestamp,
          }
        : null,
      recentBreadcrumbs: getBreadcrumbs(),
      correlationId: getCorrelationId() || undefined,
    };

    trackEvent('BlankScreenDetected', payload);
    return true;
  }

  // Se o ecrã renderizou com sucesso, limpa a última rota detetada com erro
  if (lastDetectedRoute === currentRoute) {
    lastDetectedRoute = null;
  }

  return false;
};

/**
 * Agenda a verificação de ecrã em branco com debounce pós-navegação
 */
export const scheduleWhiteScreenCheck = (
  route?: string,
  delayMs: number = 3500,
  options?: WhiteScreenOptions
): void => {
  cancelScheduledWhiteScreenCheck();

  if (typeof window === 'undefined') return;

  checkTimeoutId = setTimeout(() => {
    checkWhiteScreen(options?.rootSelector || '#root', options);
    checkTimeoutId = null;
  }, delayMs);
};

/**
 * Cancela qualquer verificação agendada pendente
 */
export const cancelScheduledWhiteScreenCheck = (): void => {
  if (checkTimeoutId !== null) {
    clearTimeout(checkTimeoutId);
    checkTimeoutId = null;
  }
};

/**
 * Redefine o estado interno do detetor (útil para testes unitários)
 */
export const resetDetectorState = (): void => {
  cancelScheduledWhiteScreenCheck();
  lastDetectedRoute = null;
};
